import { Globe, Mail, MessageCircle, Phone } from 'lucide-react';
import type { PublicProducerBranding } from '@peles/shared';

export interface ProducerBrandingPanelProps {
  branding: PublicProducerBranding;
}

/**
 * Producer marketing card shown beside the public questionnaire form:
 * logo, business name, marketing blurb, contact links, and a WhatsApp CTA.
 */
export function ProducerBrandingPanel({ branding }: ProducerBrandingPanelProps) {
  return (
    <aside
      className="bg-white border border-gray-200 border-t-4 border-t-primary-600 rounded-xl p-5 space-y-4"
      data-testid="producer-branding-panel"
    >
      <div className="flex items-center gap-3">
        {branding.logoUrl && (
          <img
            src={branding.logoUrl}
            alt={`${branding.businessName} logo`}
            className="w-12 h-12 rounded-lg object-contain bg-white border border-gray-100"
          />
        )}
        <h2 className="text-base font-bold text-primary-800">{branding.businessName}</h2>
      </div>

      {branding.marketingText && (
        <p className="text-sm text-gray-600 whitespace-pre-line">{branding.marketingText}</p>
      )}

      <div className="space-y-2 text-sm">
        {branding.phone && (
          <a
            href={`tel:${branding.phone}`}
            className="flex items-center gap-2 text-gray-700 hover:text-primary-700"
          >
            <Phone size={15} className="text-primary-600 shrink-0" />
            {branding.phone}
          </a>
        )}
        {branding.email && (
          <a
            href={`mailto:${branding.email}`}
            className="flex items-center gap-2 text-gray-700 hover:text-primary-700 break-all"
          >
            <Mail size={15} className="text-primary-600 shrink-0" />
            {branding.email}
          </a>
        )}
        {branding.website && (
          <a
            href={branding.website}
            target="_blank"
            rel="noreferrer"
            className="flex items-center gap-2 text-gray-700 hover:text-primary-700 break-all"
          >
            <Globe size={15} className="text-primary-600 shrink-0" />
            {branding.website.replace(/^https?:\/\//, '')}
          </a>
        )}
      </div>

      {branding.whatsappUrl && (
        <a
          href={branding.whatsappUrl}
          target="_blank"
          rel="noreferrer"
          className="flex items-center justify-center gap-2 w-full px-4 py-2.5 bg-green-500 text-white text-sm font-medium rounded-lg hover:bg-green-600 transition-colors"
          data-testid="whatsapp-link"
        >
          <MessageCircle size={16} />
          Message us on WhatsApp
        </a>
      )}
    </aside>
  );
}
